/**
 * LightLayer colour math (pure; mirrored exactly by the shaders in shaders.ts).
 *
 * Every colour the LightLayer paints arrives as a string from MOODS (hex or
 * rgba()) and is parsed here into straight (non-premultiplied) 0..1 floats in
 * sRGB. Mixing happens in sRGB, as CSS and MapLibre paint transitions do, so a
 * mood change on the route matches the map around it.
 */
import type { LightPalette } from "@/contracts/map";

export type RGB = [number, number, number];
export type RGBA = [number, number, number, number];

/** Distance behind the head (CSS px) over which the trail colour takes over (spec §D.8). */
export const TRAIL_PX = 480;
/** Light packet: core mixed this far toward the comet colour at the packet centre (spec §E.5: +45 %). */
export const PACKET_BRIGHTEN = 0.45;
/** Light packet: extra core width at the packet centre, CSS px (spec §E.5: +1 px). */
export const PACKET_WIDEN_PX = 1;

const clamp01 = (v: number): number => (v <= 0 ? 0 : v >= 1 ? 1 : v);

/**
 * Parses "#RGB", "#RRGGBB", "#RRGGBBAA", "rgb(r,g,b)", "rgba(r,g,b,a)" and
 * "transparent" into straight 0..1 floats. Anything unparseable is transparent
 * black (and never throws: a bad palette must not take the map down).
 */
export function parseColor(input: string | null | undefined): RGBA {
  const s = (input ?? "").trim().toLowerCase();
  if (!s || s === "transparent") return [0, 0, 0, 0];
  if (s[0] === "#") {
    let h = s.slice(1);
    if (h.length === 3 || h.length === 4) h = h.replace(/./g, "$&$&");
    if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/.test(h)) return [0, 0, 0, 0];
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16) / 255;
    return [n(0), n(2), n(4), h.length === 8 ? n(6) : 1];
  }
  const m = /^rgba?\(([^)]*)\)$/.exec(s);
  if (m) {
    const parts = m[1].split(/[\s,/]+/).filter(Boolean).map((p) => (p.endsWith("%") ? (parseFloat(p) / 100) * 255 : parseFloat(p)));
    if (parts.length < 3 || parts.slice(0, 3).some((v) => !Number.isFinite(v))) return [0, 0, 0, 0];
    const a = parts.length > 3 && Number.isFinite(parts[3]) ? clamp01(parts[3]) : 1;
    return [clamp01(parts[0] / 255), clamp01(parts[1] / 255), clamp01(parts[2] / 255), a];
  }
  return [0, 0, 0, 0];
}

export const rgb = (c: RGBA): RGB => [c[0], c[1], c[2]];

export function mixRGB(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

export function mixRGBA(a: RGBA, b: RGBA, t: number): RGBA {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];
}

/** GLSL smoothstep. */
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

/** Gaussian packet weight at `m` for a packet centred at `pulse` (metres). 0 when pulse < 0 (none). */
export function packetWeight(m: number, pulse: number, sigma: number): number {
  if (pulse < 0) return 0;
  const z = (m - pulse) / Math.max(sigma, 1);
  return Math.exp(-0.5 * z * z);
}

/**
 * The lit colour at a point of a line (CPU mirror of the fragment shader):
 *   c = mix(core, trail, smoothstep(0, 480 px, behindPx) · trailAmt)
 *   c = mix(c, comet, 0.45 · packet)
 * behindPx = (head − m) in CSS px; trailAmt 1 while drawing, easing to 0 when the head arrives.
 */
export function lineColor(core: RGB, trail: RGB, comet: RGB, behindPx: number, trailAmt: number, packet = 0): RGB {
  const tr = smoothstep(0, TRAIL_PX, behindPx) * clamp01(trailAmt);
  return mixRGB(mixRGB(core, trail, tr), comet, PACKET_BRIGHTEN * clamp01(packet));
}

/* ─── Palette ───────────────────────────────────────────────────────────── */

/** LightPalette parsed to floats (alpha is dropped: palette entries are opaque lights). */
export interface GLPalette {
  core: RGB;
  trail: RGB;
  glow: RGB;
  comet: RGB;
  halo: RGB;
  bloom: RGB;
  preview: RGB;
}

const PALETTE_KEYS = ["core", "trail", "glow", "comet", "halo", "bloom", "preview"] as const;

export function toGLPalette(p: LightPalette): GLPalette {
  const out = {} as GLPalette;
  for (const k of PALETTE_KEYS) out[k] = rgb(parseColor(p[k]));
  return out;
}

export function mixPalette(a: GLPalette, b: GLPalette, t: number): GLPalette {
  const out = {} as GLPalette;
  for (const k of PALETTE_KEYS) out[k] = mixRGB(a[k], b[k], t);
  return out;
}

/* ─── Mood light (cssVars) ──────────────────────────────────────────────── */

/** What the LightLayer reads from MOODS[m].cssVars. */
export interface MoodLight {
  shade: RGBA; //     --ld-route-shade (straight alpha)
  shadePx: number; // --ld-route-shade-px: shade width = core + this (0 = no shade)
  glowGain: number; // --ld-glow-gain
  land: RGB; //       --ld-land (fallback mode pre-composites its shade over it)
}

export function moodLightFrom(cssVars: Record<string, string> | undefined): MoodLight {
  const v = cssVars ?? {};
  const px = parseFloat(v["--ld-route-shade-px"] ?? "0");
  const gain = parseFloat(v["--ld-glow-gain"] ?? "1");
  return {
    shade: parseColor(v["--ld-route-shade"]),
    shadePx: Number.isFinite(px) ? Math.max(0, px) : 0,
    glowGain: Number.isFinite(gain) ? Math.max(0, gain) : 1,
    land: rgb(parseColor(v["--ld-land"])),
  };
}

/** Tween between two mood lights. A shade that is absent on one side keeps the other side's hue
 *  and only fades its alpha, so the golden shade never flashes black on its way out. */
export function mixMoodLight(a: MoodLight, b: MoodLight, t: number): MoodLight {
  const aHue = a.shade[3] > 0 ? a.shade : ([b.shade[0], b.shade[1], b.shade[2], 0] as RGBA);
  const bHue = b.shade[3] > 0 ? b.shade : ([a.shade[0], a.shade[1], a.shade[2], 0] as RGBA);
  const aPx = a.shade[3] > 0 ? a.shadePx : b.shadePx;
  const bPx = b.shade[3] > 0 ? b.shadePx : a.shadePx;
  const px = b.shadePx === 0 && a.shadePx === 0 ? 0 : aPx + (bPx - aPx) * t;
  return { shade: mixRGBA(aHue, bHue, t), shadePx: px, glowGain: a.glowGain + (b.glowGain - a.glowGain) * t, land: mixRGB(a.land, b.land, t) };
}

/**
 * The shade composited over the land as an OPAQUE colour. MapLibre line layers blend every
 * feature separately, so a translucent shade darkens wherever routes share a road; an opaque
 * pre-mix stays identical under any number of overlaps (fallback mode only — the GL path
 * uses MAX blending instead).
 */
export function opaqueShade(m: MoodLight): RGB {
  return mixRGB(m.land, rgb(m.shade), m.shade[3]);
}

/** "rgba(r,g,b,a)" with 0..255 channels, for MapLibre paint values in fallback mode. */
export function cssRGBA(c: RGB | RGBA, alpha = 1): string {
  const a = clamp01((c.length === 4 ? c[3] : 1) * alpha);
  const ch = (v: number) => Math.round(clamp01(v) * 255);
  return `rgba(${ch(c[0])},${ch(c[1])},${ch(c[2])},${Math.round(a * 1000) / 1000})`;
}
